import { Component } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../shared/auth.service';
import { LocalStorageService } from '../shared/local-storage.service';
import { OperatorUser } from '../shared/operator-user.model';


/**
 * Reset password component
 * We also can perform a forgot username from here.
 * We collect the user's email and the server takes things from there.
 * To reset the password the email sent code must be entered and sent with the new password.
 */
@Component({
    selector: 'app-reset-password-dialog',
    templateUrl: './reset-password-dialog.component.html',
    styleUrls: ['./reset-password-dialog.component.css'],
    standalone: false
})
export class ResetPasswordDialogComponent {

  screen: string = 'select';
  hide: boolean = true;
  isUsername: boolean = false;
  
  email = new FormControl('',[Validators.required, Validators.email]);
  username = new FormControl('',[Validators.required]);
  newPassword = new FormControl('',[Validators.required, Validators.minLength(8), Validators.maxLength(50)]);
  code = new FormControl('', [Validators.required]);

  userData!: {user: OperatorUser, pwHash: string};

  constructor(public dataSync: LocalStorageService,
     public dialogRef: MatDialogRef<ResetPasswordDialogComponent>,
      private auth: AuthService,
       private toastr: ToastrService,
        private onlineStatusService: OnlineStatusService) { }


  /**
   * Email is not recognized by the server
   */ 
  emailError(error: any): void {
    console.log("Error:", error.error);
    this.toastr.error(this.email.value! + " not found.", "Email does not exist");
  }

  /**
   * Get form details and submit the request, either forgot password or username
   */ 
  goToReset(): void {

    // if user is online
    if (this.onlineStatusService.getStatus() === OnlineStatusType.ONLINE) {

      // check if email is in the proper format
      if (this.email.valid) {

        // forgot username
        if (this.isUsername) {
          this.auth.postForgotUsername(this.email.value!).subscribe(

            // successful email entry
            () => {
              this.toastr.success('Check the email you entered for your username', 'Success!');
              this.dialogRef.close();
            },

            // email does not exist
            (error: any) => {
              this.emailError(error);
            }
          );
        }

        // password reset
        else {
          this.auth.postForgotPassword(this.email.value!).subscribe(

            // successful email entry
            () => {
              this.toastr.success('Email sent to ' + this.email.value, 'Success!');
              this.screen = 'reset';
            },

            // email does not exist
            (error: any) => {
              this.emailError(error);
            }
          );
        }  
      } 
    }

    // user cannot be offline
    else {
      this.toastr.warning('Cannot make request while offline', 'No Internet Connection');
    }
  }


  /**
   * With the reset code and new password, format the data and send a reset request to the server.
   */
  sendRequest() {
    // autofill often appends a stray space, strip it before validating or sending
    this._trimUsername();

    if (this.newPassword.valid && this.code.valid && this.username.valid) {
      let userName = this.username.value!;
      let json = {
        "password": this.newPassword.value,
        "passwordConfirmation": this.newPassword.value,
        "userName": userName,
        "resetCode": this.code.value
      };
      this.auth.postResetPassword(json).subscribe(res => {
        //Update local storage if the result is successful
        console.log(res);
        this.auth.postLogin({"password": this.newPassword.value, "username": userName}).subscribe(response => {
          this.dataSync.checkForUserInfo(response, this.newPassword.value!).then(_ => {
            this.dataSync.updateUserPassword(userName.toLowerCase(), this.newPassword.value!).then(result => {
              if (result) {
                this.toastr.success('Password changed', 'Success!');
                this.dialogRef.close();
              } else {
                this.toastr.error('Could not update password. Check for correct username and/or reset code', 'Error');
              }
            })
          });
        })
      }, _ => {
        this.toastr.error('Reset failed. Check your username and reset code are correct and try again', 'Error');
      })
    } else {
      this.toastr.error('Field(s) missing or invalid. Check all fields are properly filled out.', 'Error');
    }
  }

  /**
   * Don't allow spaces in emails
   */
  _forbidSpaces() {
    this.email.setValue(this.email.value?.replace(' ', '')!);
  }

  /**
   * Strip leading/trailing whitespace off the username.
   * Autofill commonly tacks a space onto the end, which the server rejects.
   */
  _trimUsername() {
    const trimmed = (this.username.value ?? '').trim();
    if (trimmed !== this.username.value) {
      this.username.setValue(trimmed);
    }
  }

  _forbidNonNumeric(control: AbstractControl) {
    control.setValue(control.value.replace(/[^0-9]/g, ''), {onlySelf: true, emitEvent: false});
  }
}
